import { useEffect, useState } from 'react'
import { DesignGallery } from './design/Gallery'
import { TitleScreenA } from './ui/TitleScreenA'
import { TitleScreenB } from './ui/TitleScreenB'
import { BattleScreen } from './ui/BattleScreen'
import { Encyclopedia } from './ui/Encyclopedia'
import { ShopTestScreen } from './ui/ShopTestScreen'
import { PlayerCreationScreen } from './ui/PlayerCreationScreen'
import { SoloPlayScreen } from './ui/SoloPlayScreen'
import { LobbyScreen } from './ui/LobbyScreen'
import { MultiPlayScreen } from './ui/MultiPlayScreen'
import { useMultiStore } from './multi/multiStore'
import { TintsLab } from './ui/TintsLab'
import { SettingsScreen } from './ui/SettingsScreen'
import { SfxTestScreen } from './ui/SfxTestScreen'
import { getCachedPlayer, getMe } from './game/playerApi'
import type { PlayerProfile } from '@origami/shared'

import { PixiBattle } from './pixi/PixiBattleLazy'

interface HealthResponse {
  status: string
  units: number
  timestamp: string
}

const THEME_STORAGE_KEY = 'origami-theme'

/** Resolve which title variant to render. An explicit `?title=` query
 *  param always wins; otherwise we fall back to the user's persisted
 *  preference; otherwise default to the light variant (A). */
function resolveTitleVariant(search: string): 'a' | 'b' {
  const explicit = new URLSearchParams(search).get('title')
  if (explicit === 'a' || explicit === 'b') return explicit
  if (typeof window !== 'undefined') {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (saved === 'a' || saved === 'b') return saved
  }
  return 'a'
}

/**
 * Top-level app shell.
 *
 * Routes (query-string based until a router is introduced):
 *   /                — TitleScreen (resolves to A or B from localStorage,
 *                      default A = light)
 *   /?title=a        — Force the light variant (Kakejiku Sepia Gallery)
 *   /?title=b        — Force the dark variant (Twilight Pixel Pavilion)
 *   /?solo           — production solo flow: probes /api/player/me;
 *                      routes to PlayerCreationScreen on 401 or
 *                      SoloPlayScreen on 200.
 *   /?battle         — PRIMARY battle replay: PixiJS-rendered stage
 *                      (WebGL) with shared DOM chrome; demo fixture.
 *                      Falls back to the DOM renderer when WebGL is
 *                      unavailable. /?pixibattle is a legacy alias.
 *   /?battle=real    — Pixi stage with the sim-generated fixture
 *                      (validates the live simulator end-to-end)
 *   /?battle=fx1     — ability-FX showcase battles (fx1 marquee,
 *     | fx2|fx3|fx4    fx2 swarm, fx3 waters, fx4 mirror-element
 *                      neutral matchups) — every ABILITY_FX unit
 *                      fires across them; regenerate via
 *                      packages/server/scripts/gen_fx_battles.ts
 *   /?dombattle      — classic DOM/CSS battle renderer (the WebGL
 *     [=real]          fallback), kept for comparison + regression
 *   /?design         — design gallery (mockup previews + design-system showcase)
 *   /?encyclopedia   — unit codex (every unit, all 3 star tiers, ability per star)
 *   /?shop           — shop phase test screen (mock state harness)
 *   /?tints          — paper tint comparison lab (design iteration)
 *   /?settings       — user options (paper tint picker, etc.)
 *   /?health         — backend health-check (dev sanity)
 */
export function App() {
  // Track location.search reactively so soft-navigations (history
  // pushState + a 'url-changed' custom event) re-render the route
  // without a full page navigation. The browser's fullscreen state
  // is preserved this way; an anchor's href navigation would drop it.
  const [search, setSearch] = useState(window.location.search)
  useEffect(() => {
    const onChange = () => setSearch(window.location.search)
    window.addEventListener('popstate', onChange)
    window.addEventListener('url-changed', onChange)
    return () => {
      window.removeEventListener('popstate', onChange)
      window.removeEventListener('url-changed', onChange)
    }
  }, [])

  const params = new URLSearchParams(search)
  if (params.has('solo')) return <SoloRoute />
  if (params.has('multi')) return <MultiRoute />
  // /?battle (and the legacy /?pixibattle alias) → Pixi renderer,
  // the primary battle stage. /?dombattle → classic DOM renderer,
  // kept as the fallback / comparison page.
  if (params.has('battle') || params.has('pixibattle')) return <PixiBattle />
  if (params.has('dombattle')) return <BattleScreen />
  if (params.has('design')) return <DesignGallery />
  if (params.has('encyclopedia')) return <Encyclopedia />
  if (params.has('shop')) return <ShopTestScreen />
  if (params.has('tints')) return <TintsLab />
  if (params.has('settings')) return <SettingsScreen />
  if (params.has('sfx')) return <SfxTestScreen />
  if (params.has('health')) return <HealthCheck />
  return resolveTitleVariant(search) === 'b' ? <TitleScreenB /> : <TitleScreenA />
}

/** The ?solo route handles three states: probing /api/player/me on
 *  mount, the player-creation form (cookieless or 401), and the
 *  in-game SoloPlayScreen once we have a profile in hand. */
function SoloRoute() {
  // Seed from cache when present so re-entering ?solo doesn't
  // re-flash the probing placeholder (mirrors MultiRoute).
  const initial = getCachedPlayer()
  const [phase, setPhase] = useState<'probing' | 'create' | 'play'>(
    initial ? 'play' : 'probing',
  )
  const [player, setPlayer] = useState<PlayerProfile | null>(initial)

  useEffect(() => {
    let cancelled = false
    getMe().then((res) => {
      if (cancelled) return
      if (res.ok) {
        setPlayer(res.value.player)
        setPhase('play')
      } else {
        setPhase('create')
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (phase === 'probing') {
    return (
      <div
        className="paper"
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--serif)',
          fontSize: 22,
          fontStyle: 'italic',
          color: 'var(--ink-fade)',
        }}
      >
        loosening the scroll…
      </div>
    )
  }
  if (phase === 'create' || !player) {
    return (
      <PlayerCreationScreen
        onCreated={(p) => {
          setPlayer(p)
          setPhase('play')
        }}
      />
    )
  }
  return (
    <SoloPlayScreen
      player={player}
      onSessionLost={() => setPhase('create')}
    />
  )
}

/** ?multi route — no auth gate. The multi flow now mints its own
 *  per-match Player row at lobby /create or /join time via the
 *  hero-pick inscribe step, so we don't need a pre-existing solo
 *  cookie to enter. We still probe getMe() in the background so
 *  the cached profile is available to pre-fill the inscribe form. */
function MultiRoute() {
  const cachedPlayer = getCachedPlayer()
  useEffect(() => {
    // Warm the cache for the inscribe form's pre-fill. Failure
    // (401) is fine — the form just starts blank.
    if (!cachedPlayer) void getMe()
  }, [cachedPlayer])
  return <MultiBootstrap onSessionLost={() => { /* unused */ }} />
}

/** Fans out to hub / lobby / play based on the multi store's session.
 *  No auth gate up-front — the per-match hero is inscribed in the
 *  twin-scrolls overlay when the user hosts or joins, and the server
 *  mints a Player + sets the multi cookie at that point. */
function MultiBootstrap({
  onSessionLost,
}: {
  onSessionLost: () => void
}) {
  const session = useMultiStore((s) => s.session)
  const phase = useMultiStore((s) => s.phase)
  const resume = useMultiStore((s) => s.resume)

  useEffect(() => {
    void resume()
  }, [resume])

  function exitToTitle() {
    window.history.pushState(null, '', '?')
    window.dispatchEvent(new Event('url-changed'))
  }

  // The multi-entry "hub" IS the title screen with the twin-scrolls
  // overlay. Render it immediately for any pre-match phase so the
  // navigation feels seamless; the store probe updates in the
  // background.
  if (phase === 'idle' || phase === 'loading' || phase === 'hub' || !session) {
    void exitToTitle
    return resolveTitleVariant(window.location.search) === 'b'
      ? <TitleScreenB initialMode="multi" />
      : <TitleScreenA initialMode="multi" />
  }

  if (phase === 'lobby') {
    return <LobbyScreen onSessionLost={onSessionLost} />
  }

  // shop / battle / between / spectator / finished — derive a
  // PlayerProfile shape from the local row in the lobby roster so
  // MultiPlayScreen can render avatar / name. The local player_id
  // sits on session.lobby.participants where participant_id matches
  // session.participant_id.
  const me = session.lobby.participants.find(
    (p) => p.participant_id === session.participant_id,
  )
  const player: PlayerProfile = me
    ? {
        id: me.player_id,
        display_name: me.display_name,
        team_name: me.team_name,
        icon: me.icon,
      }
    : {
        id: 'unknown',
        display_name: 'You',
        team_name: 'Your Team',
        icon: 'crane',
      }
  return (
    <MultiPlayScreen
      player={player}
      onSessionLost={onSessionLost}
      onExitToTitle={exitToTitle}
    />
  )
}

function HealthCheck() {
  const [health, setHealth] = useState<HealthResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetch('/api/health')
      .then((r) => {
        if (!r.ok) throw new Error(`Health check returned ${r.status}`)
        return r.json() as Promise<HealthResponse>
      })
      .then(setHealth)
      .catch((e) => setError(String(e)))
  }, [])

  return (
    <div
      className="paper"
      style={{
        minHeight: '100vh',
        padding: '4rem 2rem',
        fontFamily: 'var(--sans)',
        color: 'var(--ink)',
      }}
    >
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        <h1 className="cute-title" style={{ fontSize: 56, marginBottom: 16 }}>
          🦢 Origami Autobattler
        </h1>
        <p className="hand-note" style={{ fontSize: 16, marginBottom: 32 }}>
          Backend connectivity check.
        </p>
        <div className="cute-card" style={{ padding: '20px 24px' }}>
          {error && (
            <p style={{ color: 'var(--vermillion-deep)', margin: 0 }}>
              Error: {error}
            </p>
          )}
          {health && !error && (
            <>
              <p style={{ margin: '0 0 8px' }}>
                <strong>Server status:</strong> {health.status}
              </p>
              <p style={{ margin: '0 0 8px' }}>
                <strong>Units loaded:</strong> {health.units}
              </p>
              <p
                className="tiny-cap"
                style={{
                  color: 'var(--ink-fade)',
                  margin: 0,
                }}
              >
                {health.timestamp}
              </p>
            </>
          )}
          {!health && !error && <p style={{ margin: 0 }}>Loading…</p>}
        </div>
        <p
          className="hand-note"
          style={{ marginTop: 24, fontSize: 14, color: 'var(--ink-fade)' }}
        >
          Tip: visit{' '}
          <a
            href="/"
            style={{ color: 'var(--sakura-deep)', fontWeight: 600 }}
          >
            /
          </a>{' '}
          for the title screen,{' '}
          <a
            href="?design"
            style={{ color: 'var(--sakura-deep)', fontWeight: 600 }}
          >
            ?design
          </a>{' '}
          for the design gallery.
        </p>
      </div>
    </div>
  )
}
